import { RolePermissionInput } from '@/services/database';
import { PermissionAction, PermissionScope, PermissionSubject } from '@/types';

export interface RolePolicyOption extends RolePermissionInput {
    key: string;
    label: string;
}

export interface RolePolicyGroup {
    key: string;
    label: string;
    policies: RolePolicyOption[];
    children?: RolePolicyGroup[];
}

interface RolePolicySection {
    key: string;
    label: string;
    groups: RolePolicyGroup[];
}

const buildPolicyKey = (scope: PermissionScope, subject: PermissionSubject, action: PermissionAction) =>
    `${scope}:${subject}:${action}`;

const policy = (
    scope: PermissionScope,
    subject: PermissionSubject,
    action: PermissionAction,
    label: string,
): RolePolicyOption => ({
    key: buildPolicyKey(scope, subject, action),
    scope,
    subject,
    action,
    label,
});

const resourcePolicies = (
    subject: PermissionSubject,
    label: string,
    options: { includeComments?: boolean; includeSharing?: boolean } = {},
): RolePolicyGroup => ({
    key: subject,
    label,
    policies: [
        policy(PermissionScope.WORKSPACE, subject, PermissionAction.CREATE, `Create ${label}`),
        policy(PermissionScope.RESOURCE, subject, PermissionAction.READ, `View ${label}`),
        policy(PermissionScope.RESOURCE, subject, PermissionAction.UPDATE, `Edit ${label}`),
        ...(options.includeComments !== false
            ? [policy(PermissionScope.RESOURCE, subject, PermissionAction.COMMENT, `Comment on ${label}`)]
            : []),
        ...(options.includeSharing !== false
            ? [policy(PermissionScope.RESOURCE, subject, PermissionAction.INVITE, `Share ${label}`)]
            : []),
        policy(PermissionScope.RESOURCE, subject, PermissionAction.MANAGE, `Manage ${label}`),
    ],
});

const nestedPolicies = (subject: PermissionSubject, label: string): RolePolicyGroup => ({
    key: subject,
    label,
    policies: [
        policy(PermissionScope.NESTED, subject, PermissionAction.READ, `View ${label}`),
        policy(PermissionScope.NESTED, subject, PermissionAction.UPDATE, `Edit ${label}`),
        policy(PermissionScope.NESTED, subject, PermissionAction.COMMENT, `Comment on ${label}`),
        policy(PermissionScope.NESTED, subject, PermissionAction.INVITE, `Share ${label}`),
        policy(PermissionScope.NESTED, subject, PermissionAction.MANAGE, `Manage ${label}`),
    ],
});

export const countGroupPolicies = (group: RolePolicyGroup): number =>
    group.policies.length + (group.children ?? []).reduce((count, child) => count + countGroupPolicies(child), 0);

const getGroupPolicies = (group: RolePolicyGroup): RolePolicyOption[] => [
    ...group.policies,
    ...(group.children ?? []).flatMap(getGroupPolicies),
];

export const ROLE_POLICY_SECTIONS: RolePolicySection[] = [
    {
        key: 'workspace',
        label: 'Workspace',
        groups: [
            {
                key: 'workspace-core',
                label: 'Workspace',
                policies: [
                    policy(
                        PermissionScope.WORKSPACE,
                        PermissionSubject.WORKSPACE,
                        PermissionAction.READ,
                        'View Workspace',
                    ),
                    policy(
                        PermissionScope.WORKSPACE,
                        PermissionSubject.WORKSPACE,
                        PermissionAction.UPDATE,
                        'Update Workspace',
                    ),
                    policy(
                        PermissionScope.WORKSPACE,
                        PermissionSubject.ALL,
                        PermissionAction.MANAGE,
                        'Manage Workspace',
                    ),
                ],
            },
            {
                key: 'workspace-members',
                label: 'Members',
                policies: [
                    policy(PermissionScope.WORKSPACE, PermissionSubject.MEMBER, PermissionAction.READ, 'View Members'),
                    policy(PermissionScope.WORKSPACE, PermissionSubject.USER, PermissionAction.INVITE, 'Invite Users'),
                    policy(
                        PermissionScope.WORKSPACE,
                        PermissionSubject.MEMBER,
                        PermissionAction.UPDATE,
                        'Update Members',
                    ),
                ],
            },
        ],
    },
    {
        key: 'workspace-resources',
        label: 'Workspace Resources',
        groups: [
            resourcePolicies(PermissionSubject.FOLDER, 'Folders'),
            resourcePolicies(PermissionSubject.INVESTMENT, 'Investments'),
            resourcePolicies(PermissionSubject.MEMO, 'Memos'),
            resourcePolicies(PermissionSubject.PIPELINE, 'Pipelines'),
            {
                ...resourcePolicies(PermissionSubject.DASHBOARD, 'Dashboards'),
                children: [nestedPolicies(PermissionSubject.REPORT, 'Reports')],
            },
            {
                ...resourcePolicies(PermissionSubject.LIST, 'Lists'),
                children: [nestedPolicies(PermissionSubject.LIST_VIEW, 'List Views')],
            },
            resourcePolicies(PermissionSubject.TASK, 'Tasks', { includeSharing: false }),
            resourcePolicies(PermissionSubject.TEAM, 'Teams', { includeComments: false }),
        ],
    },
];

export const ROLE_POLICIES = ROLE_POLICY_SECTIONS.flatMap((section) =>
    section.groups.flatMap((group) => getGroupPolicies(group)),
);

const ROLE_POLICY_BY_KEY = new Map(ROLE_POLICIES.map((item) => [item.key, item]));

export const ROLE_POLICY_COUNT = ROLE_POLICY_BY_KEY.size;

export const isRolePolicyKey = (key: string) => ROLE_POLICY_BY_KEY.has(key);

export function getRolePermissionsForKeys(keys: string[]): RolePermissionInput[] {
    return keys.reduce<RolePermissionInput[]>((permissions, key) => {
        const selectedPolicy = ROLE_POLICY_BY_KEY.get(key);

        if (selectedPolicy) {
            permissions.push({
                scope: selectedPolicy.scope,
                subject: selectedPolicy.subject,
                action: selectedPolicy.action,
                constraints: selectedPolicy.constraints,
            });
        }

        return permissions;
    }, []);
}

export function getRolePolicyKeysForPermissions(permissions: RolePermissionInput[]): string[] {
    const selectedKeys = new Set<string>();

    permissions.forEach((permission) => {
        if (
            permission.scope === PermissionScope.WORKSPACE &&
            permission.subject === PermissionSubject.ALL &&
            permission.action === PermissionAction.MANAGE
        ) {
            ROLE_POLICIES.forEach(({ key }) => selectedKeys.add(key));
            return;
        }

        if (permission.subject === PermissionSubject.ALL && permission.action === PermissionAction.READ) {
            ROLE_POLICIES.filter(({ action }) => action === PermissionAction.READ).forEach(({ key }) =>
                selectedKeys.add(key),
            );
            return;
        }

        const exactKey = buildPolicyKey(permission.scope, permission.subject, permission.action);
        if (ROLE_POLICY_BY_KEY.has(exactKey)) {
            selectedKeys.add(exactKey);
            return;
        }

        if (permission.subject === PermissionSubject.USER && permission.action === PermissionAction.READ) {
            selectedKeys.add(
                buildPolicyKey(PermissionScope.WORKSPACE, PermissionSubject.MEMBER, PermissionAction.READ),
            );
        }
    });

    return Array.from(selectedKeys);
}
